/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-05-25 17:45:06
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import queryString from 'query-string';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Page, Pagination, TextField, Layout, Card, Icon } from '@shopify/polaris';
import { SearchMinor, CancelSmallMinor } from '@shopify/polaris-icons';
import { withI18n } from '@shopify/react-i18n';
import {
  reduxForm,
  reset,
  getFormValues,
  FieldArray,
  change,
  getFormSyncErrors,
  stopSubmit
} from 'redux-form';

import MainMenu from 'src/components/containers/main-menu';
import { NUMBER_SWATCHES_PER_PAGE } from 'src/constants/swatch-settings';
import { URL } from 'src/constants/links';
import DidYouKnow from 'src/components/views/swatch-settings/did-you-know';
import ListSwatches from 'src/components/views/swatch-settings/list-swatches';
import ActionsGroup from 'src/components/views/swatch-settings/actions-group';
import InvalidInputBanner from 'src/components/views/swatch-settings/invalid-input-banner';
import RouteLeavingGuard from 'src/components/views/common/handle-leave/route-leaving-guard';
import ConfirmLeaveModal from 'src/components/views/common/handle-leave/confirm-leave-modal';
import SendRequestFeatureModal from 'src/components/containers/common/send-request-feature-modal';
import RequestResultModal from 'src/components/containers/common/request-result-modal';
import TypeFormEmbeddedWithButton from 'src/components/views/common/typeform-embedded-with-button';
import {
  buildInitialValues,
  buildSwatchSettingsValues,
  checkValidateSettings
} from 'src/helpers/swatch-settings-helper';
import { searchify, confirmLeavePage } from 'src/helpers';
import history from 'src/routes/history';
import ExceededPlanNotification from 'src/components/containers/common/exceeded-plan-notifications';
import ThemeUpgradeFlow2Notification from 'src/components/containers/common/theme-upgrade-flow-2-notification';
import InstallationRemindStatusBanner from 'src/components/containers/common/installation-remind-status-banner';
import FilterActionList from 'src/components/views/swatch-settings/filter-action-list';
import { FEATURE_IDS } from 'src/constants/theme-upgrade';

import * as swatchSettingsActions from 'src/stores/actions/swatch-settings-actions';
import * as quickStartGuideAction from 'actions/dashboard/quick-start-guide-action';

import TitleHelp from 'src/components/views/common/title-help';

import type { ComposedProps, State } from './index.type';
import { routes } from 'src/routes';
class SwatchSettings extends Component<ComposedProps, State> {
  timeoutActionSurvey = undefined;
  timerTyping = undefined;

  state = {
    // Search string
    inputSearchText: '',

    skip: 0,
    limit: NUMBER_SWATCHES_PER_PAGE,

    sortId: 'name',
    isAsc: false,
    hasPrevious: false,
    hasNext: false,

    // Show/Hide error banner
    dismissedBanner: true,
    messageError: [],
    swatchSettingsError: [],
    toggleSubmit: false,

    // Show/Hide confirm reload list when searching/changing page/sorting
    activeModalConfirm: false,
    actionToGo: (params = {}) => {},
    params: {},

    // Check go next/prev page
    paginating: false,

    // Modal send request result
    activeRequestModal: false,
    activeModalResult: false,

    // Survey
    // actionSurvey: false

    searchOption: 'any'
  };

  componentDidMount() {
    const { getSwatchSettings } = this.props;

    // Run survey
    // this.performActionSurvey();

    // Get list for selected page
    const queries = queryString.parse(this.props.location.search);
    let {
      q = '',
      skip = 0,
      limit = NUMBER_SWATCHES_PER_PAGE,
      sortId = 'name',
      isAsc = false,
      searchOption = 'any'
    } = queries;
    skip = parseInt(skip);
    limit = parseInt(limit);

    this.setState({ inputSearchText: q, skip, limit, sortId, isAsc, searchOption });

    // Get Swatch settings for first page
    getSwatchSettings(encodeURIComponent(q), skip, limit, sortId, isAsc, searchOption);

    // Handle when reload or leave page
    window.addEventListener('beforeunload', this.confirmLeavePage);
  }

  componentWillUnmount() {
    // Handle when reload or leave page
    window.removeEventListener('beforeunload', this.confirmLeavePage);
  }

  componentWillReceiveProps(nextProps, nextState) {
    const { skip, limit } = this.state;
    const { total, submitFailed } = this.props;
    const {
      total: nextTotal,
      skip: nextSkip,
      submitFailed: nextSubmitFailed,
      stopSubmitForm,
      synchronousError
    } = nextProps;

    if (skip !== nextSkip || total !== nextTotal) {
      const hasNext = skip + limit < total;
      const hasPrevious = skip > 0;

      let updatedState = { hasNext, hasPrevious };
      this.setState(updatedState);
    }

    if (!submitFailed && nextSubmitFailed) {
      const { toggleSubmit } = this.state;
      const messageError = this.renderError(synchronousError);

      // Stop submit form
      stopSubmitForm();

      this.setState({
        messageError,
        dismissedBanner: !_.isEmpty(messageError) ? false : true,
        toggleSubmit: !_.isEmpty(messageError) ? !toggleSubmit : toggleSubmit
      });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { toggleSubmit, paginating } = this.state;
    const { toggleSubmit: prevToggleSubmit } = prevState;
    const { getLoading } = this.props;
    const { getLoading: prevGetLoading } = prevProps;

    if (toggleSubmit !== prevToggleSubmit || (!getLoading && prevGetLoading && paginating)) {
      this.setState({ paginating });
      window.scrollTo(0, 0);
    }
  }

  render() {
    const {
      i18n,
      submitting,
      handleSubmit,
      pristine,
      getLoading,
      saving,
      formValues,
      total,
      updateSwatchField,
      dirty,
      isSearchResult,
      selectedPlan,
      email,
      tenantId
    } = this.props;

    const {
      inputSearchText,
      skip,
      limit,
      sortId,
      isAsc,
      hasPrevious,
      hasNext,
      dismissedBanner,
      messageError,
      activeModalConfirm,
      swatchSettingsError,
      activeRequestModal,
      activeModalResult,
      // actionSurvey
      searchOption
    } = this.state;

    return (
      <div className="BC-Page BC-Page__SwatchSettings">
        <MainMenu menuId="menu-filters-swatch-settings" />
        <Page
          title={i18n.translate('AdminHTML.SwatchSettings.SwatchSettings.title')}
          primaryAction={
            total !== 0 && (
              <div className="BC-Page BC-Page__Button">
                <ActionsGroup
                  handleSubmit={handleSubmit(this.handleSave.bind(this))}
                  handleCancel={this.discardChange}
                  pristine={pristine}
                  submitting={submitting}
                  saving={saving}
                />
              </div>
            )
          }>
          <TitleHelp
            i18n={i18n}
            hrefUserManual={URL['Link_V2_Filter_SwatchSetting_ManualGuide']}
            onAnalytics={() => history.push(routes.analytics.overviewCollection())}
          />
          <Layout>
            <Layout.Section>
              <div className="BC-Container__Notifications">
                {/* Installation reminder status banner */}
                <InstallationRemindStatusBanner />

                <ThemeUpgradeFlow2Notification featureID={FEATURE_IDS.SWATCH_SETTINGS} />

                {/* Did you know? */}
                <DidYouKnow sendRequest={() => this.toggleModal('activeRequestModal')} />

                {/* Sticky notification for exceeded plan */}
                <ExceededPlanNotification />

                {/* Banner error */}
                {!dismissedBanner && (
                  <InvalidInputBanner
                    onDismiss={this.handleDismissBanner}
                    messages={messageError}
                  />
                )}
              </div>
            </Layout.Section>

            {/* List of swatchSettings */}
            <Layout.Section>
              <Card sectioned>
                <Card.Header>
                  <div className="search" onKeyDown={this.handleKeyDown} onKeyUp={this.handleKeyUp}>
                    <TextField
                      onChange={this.onSearch}
                      value={inputSearchText}
                      prefix={<Icon source={SearchMinor} color="base" />}
                      placeholder={i18n.translate(
                        'AdminHTML.SwatchSettings.SwatchSettings.inputSearchText'
                      )}
                    />

                    {/** Select search option */}
                    <FilterActionList
                      i18n={i18n}
                      selected={searchOption}
                      onSelect={this.onSelectSearchOption}
                    />
                  </div>

                  {/** Display selected option */}
                  {searchOption !== 'any' && (
                    <div className="searchOption">
                      <span className="item">
                        <span className="value">
                          {searchOption === 'complete'
                            ? i18n.translate(
                                'AdminHTML.SwatchSettings.SwatchSettings.searchCompleteDes'
                              )
                            : i18n.translate(
                                'AdminHTML.SwatchSettings.SwatchSettings.searchMissingDes'
                              )}
                        </span>
                        <span className="remove" onClick={this.onRemoveSearchOption}>
                          <Icon source={CancelSmallMinor} color="base" />
                        </span>
                      </span>
                    </div>
                  )}
                </Card.Header>
                <form onSubmit={this.handleDefaultSubmit} className="productRanking">
                  <FieldArray
                    name="swatchSettings"
                    component={ListSwatches}
                    loading={getLoading}
                    skip={skip}
                    limit={limit}
                    sortId={sortId}
                    isAsc={isAsc}
                    list={formValues ? formValues['swatchSettings'] : []}
                    onSort={(sortId, isAsc) => this.confirmLeave('sort', { sortId, isAsc })}
                    updateSwatchField={updateSwatchField}
                    isSearchResult={isSearchResult}
                    swatchSettingsError={swatchSettingsError}
                  />
                </form>
              </Card>
            </Layout.Section>
          </Layout>

          {/* Pagination*/}
          <div className="pagination">
            {(hasPrevious || hasNext) && (
              <Pagination
                hasPrevious={hasPrevious}
                onPrevious={() => this.confirmLeave('previousPage')}
                hasNext={hasNext}
                onNext={() => this.confirmLeave('nextPage')}
              />
            )}

            {total !== 0 && (
              <div className="actionsGroup">
                <ActionsGroup
                  handleSubmit={handleSubmit(this.handleSave.bind(this))}
                  handleCancel={this.discardChange}
                  pristine={pristine}
                  submitting={submitting}
                  saving={saving}
                />
              </div>
            )}
          </div>
        </Page>

        {/* Confirm before leaving */}
        <RouteLeavingGuard
          when={dirty}
          // Navigate function
          navigate={path => history.push(path)}
          shouldBlockNavigation={dirty}
        />
        <ConfirmLeaveModal
          active={activeModalConfirm}
          handleCancel={this.closeModal}
          handleDiscard={this.handleDiscard}
        />

        <SendRequestFeatureModal
          active={activeRequestModal}
          handleClose={() => this.toggleModal('activeRequestModal')}
          handleOpenResultModal={() => this.toggleModal('activeModalResult')}
          featureKey="swatchSettings"
        />
        <RequestResultModal
          active={activeModalResult}
          handleClose={() => this.toggleModal('activeModalResult')}
          featureName={i18n.translate(
            'AdminHTML.Common.RequestResultModal.content_t1_swatchSettings'
          )}
        />
        {!_.isEmpty(selectedPlan) && (
          <TypeFormEmbeddedWithButton
            type="swatch-settings"
            // conditionShowStateChange={actionSurvey}
            email={email}
            tenantId={tenantId}
          />
        )}
      </div>
    );
  }

  onSelectSearchOption = option => {
    const { searchOption } = this.state;

    // No change => return
    if (searchOption === option) return;

    // Update state
    this.setState({ searchOption: option });

    // Start searching
    setTimeout(() => {
      this.handleSearch();
    });
  };

  onRemoveSearchOption = () => {
    // Search all values
    this.onSelectSearchOption('any');
  };

  // Run survey when after stay this page 30s
  // performActionSurvey = () => {
  //   const { actionSurvey } = this.state;

  //   clearTimeout(this.timeoutActionSurvey);

  //   this.timeoutActionSurvey = setTimeout(() => {
  //     this.setState({ actionSurvey: !actionSurvey });
  //   }, 30000);
  // };

  // Key press event on search field
  handleKeyDown = event => {
    clearTimeout(this.timerTyping);
  };

  // Key up event, start searching after 500ms
  handleKeyUp = event => {
    const charList = 'abcdefghijklmnopqrstuvwxyz0123456789';
    const key = event.key.toLowerCase();
    const keyCode = event.keyCode;

    if (charList.indexOf(key) === -1 && keyCode !== 8) {
      return;
    }

    // Invoke search request after 500ms
    this.timerTyping = setTimeout(() => {
      this.confirmLeave('search');
    }, 500);
  };

  // On search text change
  onSearch = inputSearchText => {
    this.setState({ inputSearchText });
  };

  // Search ranking
  handleSearch = () => {
    let inputSearchText = this.state.inputSearchText.toLowerCase().trim();
    let searchOption = this.state.searchOption;

    const { sortId, isAsc } = this.state;
    inputSearchText = searchify(inputSearchText);

    // Having search text, search custom filter for page 1
    if (inputSearchText) {
      // Set limit per page
      this.setState({ skip: 0, limit: NUMBER_SWATCHES_PER_PAGE });

      this.props.getSwatchSettings(
        encodeURIComponent(inputSearchText),
        0,
        NUMBER_SWATCHES_PER_PAGE,
        sortId,
        isAsc,
        searchOption
      );
    } else {
      // Search text is empty, get all filter tree for page 1
      const skip = 0,
        limit = NUMBER_SWATCHES_PER_PAGE;

      // Set skip, limit for page 1
      this.setState({ skip, limit });

      // Get filter trees
      this.props.getSwatchSettings('', skip, limit, sortId, isAsc, searchOption);
    }

    // Reset error
    this.setState({ dismissedBanner: true, messageError: [] });
  };

  // Go to previous page
  getPreviousPage = () => {
    const { skip, limit, sortId, isAsc } = this.state;
    let { inputSearchText, searchOption } = this.state;
    const previousSkip = skip - limit;
    const previousLimit = NUMBER_SWATCHES_PER_PAGE;

    // Prevent multiple click that the skip number is less than 0
    if (previousSkip < 0) return;

    // Searchify input search
    inputSearchText = inputSearchText.toLowerCase().trim();
    inputSearchText = searchify(inputSearchText);

    // Set skip, limit for next page
    this.setState({ skip: previousSkip, limit: previousLimit, paginating: true });
    this.props.getSwatchSettings(
      encodeURIComponent(inputSearchText) || '',
      previousSkip,
      NUMBER_SWATCHES_PER_PAGE,
      sortId,
      isAsc,
      searchOption
    );

    // Reset error
    this.setState({ dismissedBanner: true, messageError: [] });
  };

  // Go to next page
  getNextPage = () => {
    const { skip, limit, sortId, isAsc } = this.state;
    let { inputSearchText, searchOption } = this.state;
    const { total } = this.props;
    const nextSkip = skip + limit;

    // Prevent multiple click that the skip number is bigger than total
    if (nextSkip >= total) return;

    // Searchify input search
    inputSearchText = inputSearchText.toLowerCase().trim();
    inputSearchText = searchify(inputSearchText);

    // Set skip, limit for next page and disable flag for first page loading, don't need to display skeleton loading
    this.setState({ skip: nextSkip, limit, paginating: true });
    this.props.getSwatchSettings(
      encodeURIComponent(inputSearchText) || '',
      nextSkip,
      NUMBER_SWATCHES_PER_PAGE,
      sortId,
      isAsc,
      searchOption
    );

    // Reset error
    this.setState({ dismissedBanner: true, messageError: [] });
  };

  onSort = params => {
    const { sortId, isAsc } = params;
    let { limit, inputSearchText, searchOption } = this.state;

    // Searchify input search
    inputSearchText = inputSearchText.toLowerCase().trim();
    inputSearchText = searchify(inputSearchText);

    // Set sort id, asc
    this.setState({ sortId, isAsc, skip: 0 });
    this.props.getSwatchSettings(
      encodeURIComponent(inputSearchText) || '',
      0,
      limit,
      sortId,
      isAsc,
      searchOption
    );

    // Reset error
    this.setState({ dismissedBanner: true, messageError: [] });
  };

  // Save productRanking
  handleDefaultSubmit = e => {
    e.preventDefault();
  };

  // Handle click save button
  handleSave = data => {
    const { i18n, updateSwatchSettings, markDoneStep } = this.props;
    const { swatchSettings } = data;

    const { globalError = [], swatchSettingsError = [] } = checkValidateSettings(
      swatchSettings,
      i18n
    );
    if (!_.isEmpty(globalError)) {
      this.setState({
        dismissedBanner: false,
        messageError: globalError,
        swatchSettingsError,
        toggleSubmit: !this.state.toggleSubmit
      });
      return;
    }

    this.setState({ dismissedBanner: true, messageError: [], swatchSettingsError: [] });

    markDoneStep('V2_FILTER5_ADVANCED', {
      isDone: true
    });
    const saveData = buildSwatchSettingsValues(swatchSettings);
    updateSwatchSettings(saveData);
  };

  // Handle hide banner
  handleDismissBanner = () => {
    // Hide banner
    this.setState({ dismissedBanner: true, messageError: [] });
  };

  // Handle discard button
  discardChange = () => {
    const { resetForm } = this.props;

    resetForm();

    // Reset message error
    this.setState({ dismissedBanner: true, messageError: [], swatchSettingsError: [] });
  };

  // Handle confirm leave modal
  // Close modal
  closeModal = () => {
    this.setState({ activeModalConfirm: false, actionToGo: (params = {}) => {}, params: {} });
  };

  // Handle confirm leave modal
  // Discard change, continue action
  handleDiscard = () => {
    const { params, actionToGo } = this.state;

    // Reset form
    this.discardChange();

    // Continue action
    if (!_.isEmpty(params)) {
      actionToGo(params);
    } else {
      actionToGo();
    }

    // Close modal
    this.closeModal();
  };

  // Handle confirm leave
  confirmLeave = (action, params = {}) => {
    const { dirty } = this.props;
    let callBack = (params = {}) => {};
    switch (action) {
      case 'sort': {
        callBack = this.onSort;
        break;
      }
      case 'search': {
        callBack = this.handleSearch;
        break;
      }
      case 'nextPage': {
        callBack = this.getNextPage;
        break;
      }
      case 'previousPage': {
        callBack = this.getPreviousPage;
        break;
      }
      default:
    }
    if (dirty) {
      this.setState({ activeModalConfirm: true, actionToGo: callBack, params: params });
    } else {
      // $FlowFixMe
      callBack(params);
    }
  };

  renderError = synchronousError => {
    let globalError = [];
    const { swatchSettings = [] } = synchronousError;
    swatchSettings.map((swatchSetting, index) => {
      if (!_.isEmpty(swatchSetting)) {
        let { imageUrl = '' } = swatchSetting;
        if (!_.isEmpty(imageUrl)) {
          if (!globalError.includes(imageUrl)) {
            globalError.push(imageUrl);
          }
        }
      }
      return '';
    });
    return globalError;
  };

  toggleModal = fieldName => {
    const value = this.state[fieldName];
    this.setState({ [fieldName]: !value });
  };

  // Handle confirm leave page
  confirmLeavePage = event => {
    const { dirty, i18n } = this.props;
    return confirmLeavePage(event, dirty, i18n);
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  getSwatchSettings: (q, skip, limit, sortId, isAsc, searchOption) =>
    dispatch(
      swatchSettingsActions.getSwatchSettings(
        q,
        skip,
        limit,
        sortId,
        isAsc,
        searchOption,
        ownProps.i18n
      )
    ),
  updateSwatchSettings: swatchSettings =>
    dispatch(swatchSettingsActions.updateSwatchSettings(swatchSettings, undefined, ownProps.i18n)),
  resetForm: () => dispatch(reset('SwatchSettingsForm')),
  stopSubmitForm: () => dispatch(stopSubmit('SwatchSettingsForm')),
  updateSwatchField: (index, name, value) =>
    dispatch(change('SwatchSettingsForm', `swatchSettings[${index}][${name}]`, value)),
  markDoneStep: (stepName, value) =>
    dispatch(quickStartGuideAction.markDoneStep(stepName, value, ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  const formValues = getFormValues('SwatchSettingsForm')(state);
  const swatchSettings = _.get(state.swatchSettings, 'swatchSettings', []);
  const initialValues = buildInitialValues(swatchSettings);
  const tenantApp = _.get(state.oauth, 'tenantApp', {});
  const externalInfo = _.get(tenantApp, 'externalInfo', {});

  return {
    getLoading: _.get(state.swatchSettings, 'getLoading', false),
    saving: _.get(state.swatchSettings, 'updateLoading', false),
    success: _.get(state.swatchSettings, 'success', false),
    total: _.get(state.swatchSettings, 'total', 0),
    isSearchResult: _.get(state.swatchSettings, 'isSearchResult', false),
    formValues,
    swatchSettings,
    initialValues,
    synchronousError: getFormSyncErrors('SwatchSettingsForm')(state),
    selectedPlan: _.get(state.oauth.tenantApp, 'selectedPlan'),
    email: _.get(externalInfo, 'email', ''),
    tenantId: _.get(tenantApp, 'tenantId', '')
  };
};

const SwatchSettingsForm = reduxForm({
  form: 'SwatchSettingsForm', // a unique identifier for this form
  enableReinitialize: true
})(SwatchSettings);

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(SwatchSettingsForm));
